import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AlertCircle, Bell, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Button, Card } from "../../shared/ui";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Compact list of workers whose recent timesheet is incomplete/missed.
 *  Shown on the Dashboard to admins/managers/office/supervisors. */
export const MissedWeeksTile = () => {
  const [data, setData] = useState({ count: 0, workers: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/missed-weeks`);
      setData(r.data || { count: 0, workers: [] });
    } catch (e) {
      // Non-blocking — most likely 403 if user role isn't allowed
      setData({ count: 0, workers: [] });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remind = async (tsId, userName, weekStart) => {
    setSending(prev => ({ ...prev, [tsId]: true }));
    try {
      await axios.post(`${API}/dashboard/missed-weeks/${tsId}/remind`);
      toast.success(`Reminder sent to ${userName} for ${weekStart}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send reminder");
    } finally {
      setSending(prev => ({ ...prev, [tsId]: false }));
    }
  };

  const [rebuilding, setRebuilding] = useState(false);
  const rebuildAll = async () => {
    if (!window.confirm("Rebuild every worker's timesheet for the last 8 weeks from clock-in/out records? This is safe to re-run.")) return;
    setRebuilding(true);
    try {
      const r = await axios.post(`${API}/admin/rebuild-timesheets-from-entries?weeks_back=8`);
      toast.success(r.data.message || "Rebuild complete");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rebuild failed");
    } finally { setRebuilding(false); }
  };

  if (loading || data.count === 0) return null;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
    } catch { return iso; }
  };

  return (
    <Card className="border-2 border-amber-300 bg-amber-50" data-testid="missed-weeks-tile">
      <button
        type="button"
        onClick={() => setExpanded(s => !s)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="missed-weeks-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-200 text-amber-900">
            <AlertCircle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm uppercase tracking-tight text-amber-900">
              {data.count} worker{data.count === 1 ? "" : "s"} need chasing
            </h3>
            <p className="text-xs text-amber-700">
              Incomplete / auto-submitted timesheets in the last {data.weeks_back} weeks
            </p>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-5 h-5 text-amber-700" /> : <ChevronRight className="w-5 h-5 text-amber-700" />}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-amber-200 space-y-2" data-testid="missed-weeks-list">
          {data.workers.map(w => (
            <div key={w.user_id} className="bg-white rounded border border-amber-200 p-2" data-testid={`missed-worker-${w.user_id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-bold text-sm text-slate-800">{w.user_name}</p>
                <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                  {w.weeks.length} week{w.weeks.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="space-y-1">
                {w.weeks.map(week => {
                  const tags = [];
                  if (week.total_hours === 0) tags.push("0h");
                  else tags.push(`${week.total_hours.toFixed(1)}h`);
                  if (week.travel_hours > 0) tags.push(`${week.travel_hours.toFixed(1)}h travel`);
                  if (week.laa_days > 0) tags.push(`${week.laa_days} LAA`);
                  if (week.auto_submitted) tags.push("auto-submitted");
                  if (week.incomplete) tags.push("incomplete");
                  return (
                    <div key={week.timesheet_id} className="flex items-center justify-between gap-2 text-xs">
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-slate-700">Wk of {fmtDate(week.week_start)}</span>
                        <span className="text-slate-500"> · {tags.join(" · ")}</span>
                        {week.reminder_sent_at && (
                          <span className="text-[10px] italic text-green-700 ml-1">· reminder sent</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => remind(week.timesheet_id, w.user_name, fmtDate(week.week_start))}
                        disabled={sending[week.timesheet_id]}
                        className="text-[11px] font-bold uppercase text-amber-700 hover:text-amber-900 hover:underline disabled:opacity-50"
                        data-testid={`remind-${week.timesheet_id}`}
                      >
                        <Bell className="w-3 h-3 inline mr-0.5" />
                        {sending[week.timesheet_id] ? "Sending…" : (week.reminder_sent_at ? "Remind again" : "Remind")}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <Link to="/timesheets" className="block text-center text-xs font-bold text-amber-700 hover:text-amber-900 hover:underline mt-2" data-testid="missed-weeks-open-timesheets">
            Open Timesheets → Pending Approval <ExternalLink className="w-3 h-3 inline ml-0.5" />
          </Link>
          <button
            type="button"
            onClick={rebuildAll}
            disabled={rebuilding}
            className="w-full mt-2 text-center text-[11px] font-bold uppercase text-slate-700 hover:text-slate-900 border border-slate-300 hover:bg-slate-100 rounded py-1.5 disabled:opacity-50"
            data-testid="rebuild-timesheets-btn"
            title="Rebuilds every worker's last 8 weeks of timesheets from raw clock-in/out records. Safe to re-run."
          >
            {rebuilding ? "Rebuilding..." : "Rebuild last 8 weeks from clock-ins"}
          </button>
        </div>
      )}
    </Card>
  );
};

export default MissedWeeksTile;
